import { type Ati } from '@atlassian/ari';

import type { MenuItem, MixedMenuItems, SimpleMenuItems, UniversalCreateItems } from './types';
import type { MenuSectionV2, NativeMenuItem } from './types/menuV2';

export function isSimpleMenuItems(items: UniversalCreateItems): items is SimpleMenuItems {
	if (Array.isArray(items)) {
		return items.every((item) => {
			return typeof item === 'object' && item !== null && 'ati' in item;
		});
	}

	return false;
}

export function useNormalizeItems(items: UniversalCreateItems): MixedMenuItems {
	const mixedItems: MixedMenuItems = [];
	if (isSimpleMenuItems(items)) {
		mixedItems.push({
			items,
		});
	} else {
		mixedItems.push(...items);
	}

	return mixedItems;
}

export function findSelectedItem(items: MixedMenuItems, selectedItem: Ati): MenuItem | null {
	for (const namedItem of items) {
		for (const menuItem of namedItem.items) {
			if (menuItem.ati.toString() === selectedItem.toString()) {
				return menuItem;
			}
		}
	}
	return null;
}

/**
 * V2 version of findSelectedItem that searches through MenuSectionV2 array.
 * Searches app-defined sections for items matching the selected ATI.
 * Note: Suggestion sections don't contain items at definition time, so they're skipped.
 *
 * @param sections - Array of MenuSectionV2 to search through
 * @param selectedItem - The ATI to search for
 * @returns The matching NativeMenuItem or null if not found
 */
export function findSelectedItemV2(
	sections: MenuSectionV2[],
	selectedItem: Ati,
): NativeMenuItem | null {
	for (const section of sections) {
		// Only app-defined sections have items at definition time
		if (section.type === 'appDefinedSection') {
			for (const menuItem of section.items) {
				// Check if item has ati property (both NativeMenuItem and CrossAppMenuItemV2 have it)
				if ('ati' in menuItem && menuItem.ati.toString() === selectedItem.toString()) {
					// For cross-app items, we need to return null as they don't have onClick/loadCreateContent
					// Only native items can be "selected" in the traditional sense
					if ('onClick' in menuItem || 'loadCreateContent' in menuItem) {
						return menuItem;
					}
				}
			}
		}
	}
	return null;
}

/**
 * Extracts all ATIs from the mixed menu items
 * @param mixedItems - The mixed menu items to extract ATIs from
 * @returns Array of ATI strings
 */
export function extractNativeAtis(mixedItems: MixedMenuItems): string[] {
	return mixedItems
		.flatMap((namedItem) => namedItem.items)
		.map((menuItem) => menuItem.ati.toString());
}
